export const CLAIM_NAMESPACE = 'https://arc.everservice.1p21.io/claims/'
export const GOOGLE_CONNECTION = 'google-oauth2'
const CLOCK_TOLERANCE_SECONDS = 60

export class AuthMigrationError extends Error {
  constructor (code, message, { retryable = false, action = 'support' } = {}) {
    super(message)
    this.name = 'AuthMigrationError'
    this.code = code
    this.retryable = retryable
    this.action = action
  }
}

export function requestError (error, message = 'ARC could not verify your account. Please try again.') {
  if (error instanceof AuthMigrationError) return error
  const status = error && error.response && error.response.status
  const retryable = !status || status === 408 || status === 429 || status >= 500
  return new AuthMigrationError('ACCOUNT_REQUEST_FAILED', message, {
    retryable,
    action: retryable ? 'retry' : 'support'
  })
}

export function errorDetails (error) {
  const safe = requestError(error)
  return { code: safe.code, message: safe.message, retryable: safe.retryable, action: safe.action }
}

export function freshLoginProof (claims, startedAt, now = Date.now() / 1000) {
  const authTime = claims && claims.auth_time
  if (!Number.isFinite(startedAt) || startedAt <= 0 || startedAt > now + CLOCK_TOLERANCE_SECONDS ||
      !Number.isFinite(authTime) || authTime < startedAt - CLOCK_TOLERANCE_SECONDS ||
      authTime > now + CLOCK_TOLERANCE_SECONDS || !claims.sub) {
    throw new AuthMigrationError('FRESH_LOGIN_REQUIRED', 'Please sign in with Google again to finish connecting your ARC account.', { action: 'google-login' })
  }
  return { subject: claims.sub, authTime, googleId: claims[CLAIM_NAMESPACE + 'google_id'] }
}

function migrationRequired () {
  return new AuthMigrationError('ACCOUNT_NOT_LINKED', 'Migrate your ARC account in the migration portal and wait for approval before signing in with Google.', { action: 'migrate-account' })
}

export function resolveMigrationIdentity ({ user, claims, managementUser, freshLogin }) {
  const identities = Array.isArray(managementUser?.identities) ? managementUser.identities : []
  if (!user || !claims || typeof user.sub !== 'string' || claims.sub !== user.sub || managementUser?.user_id !== user.sub) {
    throw new AuthMigrationError('IDENTITY_MISMATCH', 'The sign-in identity could not be verified. Contact the migration administrator.')
  }
  if (managementUser.blocked) {
    throw new AuthMigrationError('ACCOUNT_BLOCKED', 'This account is blocked. Contact the migration administrator.')
  }
  if (user.sub.startsWith('google-oauth2|') && !identities.some(identity => identity.provider === 'waad')) {
    throw migrationRequired()
  }
  if (!user.sub.startsWith('waad|') || identities[0]?.provider !== 'waad') {
    throw new AuthMigrationError('MICROSOFT_PRIMARY_REQUIRED', 'The original Microsoft-primary account could not be verified. Contact the migration administrator.')
  }
  const oid = managementUser.oid
  if (typeof oid !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(oid)) {
    throw new AuthMigrationError('MICROSOFT_ID_MISSING', 'The original Microsoft identifier is missing. Contact the migration administrator.')
  }
  const googleIdentities = identities.filter(identity => identity.provider === GOOGLE_CONNECTION)
  if (googleIdentities.length === 0) throw migrationRequired()
  if (googleIdentities.length !== 1) {
    throw new AuthMigrationError('GOOGLE_IDENTITY_UNRESOLVED', 'ARC needs exactly one approved Google identity linked to your Microsoft account. Contact the migration administrator.')
  }
  const google = googleIdentities[0]
  if (google.connection !== GOOGLE_CONNECTION || typeof google.user_id !== 'string' ||
      !google.user_id || google.user_id.length > 50 || google.user_id.trim() !== google.user_id) {
    throw new AuthMigrationError('GOOGLE_IDENTITY_INVALID', 'The linked Google identity could not be verified. Contact the migration administrator.')
  }
  verifyGoogleClaim({ claims, freshLogin }, google.user_id)
  return { microsoftId: oid, googleId: google.user_id }
}

export function verifyGoogleClaim ({ claims, freshLogin }, googleId) {
  if (claims[CLAIM_NAMESPACE + 'connection'] !== GOOGLE_CONNECTION ||
      claims[CLAIM_NAMESPACE + 'connection_strategy'] !== GOOGLE_CONNECTION ||
      !claims[CLAIM_NAMESPACE + 'google_id']) {
    if (freshLogin) {
      throw new AuthMigrationError('GOOGLE_CLAIMS_MISSING', 'Google sign-in did not provide ARC’s migration claims. Ask the administrator to check the ARC Auth0 Action.')
    }
    throw new AuthMigrationError('FRESH_LOGIN_REQUIRED', 'Sign in with Google to verify your linked ARC account.', { action: 'google-login' })
  }
  if (claims[CLAIM_NAMESPACE + 'google_id'] !== googleId) {
    throw new AuthMigrationError('GOOGLE_IDENTITY_CONFLICT', 'The Google login and linked account disagree. Contact the migration administrator.')
  }
}

export function requireFreshLogin ({ user, claims, freshLogin }, googleId) {
  if (!freshLogin || freshLogin.subject !== user.sub || freshLogin.authTime !== claims.auth_time ||
      freshLogin.googleId !== googleId) {
    throw new AuthMigrationError('FRESH_LOGIN_REQUIRED', 'Sign in with Google again before ARC saves your Google account ID.', { action: 'google-login' })
  }
}

function assertAccount (user, expected) {
  if (!user || user.id !== expected.id || user.ms_graph_id !== expected.ms_graph_id || user.api_key !== expected.api_key) {
    throw new AuthMigrationError('ARC_ACCOUNT_CHANGED', 'ARC’s account identifiers or credentials changed during verification. Contact the migration administrator.')
  }
  if (!Object.prototype.hasOwnProperty.call(user, 'google_id') ||
      (user.google_id !== null && typeof user.google_id !== 'string')) {
    throw new AuthMigrationError('API_NOT_READY', 'This ARC API does not expose a supported Google ID field. Contact the administrator.')
  }
  return user
}

function needsUpdate (user, googleId) {
  if (user.google_id === googleId) return false
  if (user.google_id === null || user.google_id === '') return true
  throw new AuthMigrationError('ARC_GOOGLE_ID_CONFLICT', 'This ARC account has a different Google ID. Contact the migration administrator.')
}

// Each attempt starts with exact lookup and a read authenticated as that ARC user.
// After an uncertain PATCH, another successful read is required before any retry.
export async function synchronizeGoogleId ({ lookupClient, createUserClient, identity, assertFresh, baselineState = {}, assertCurrent = () => {} }) {
  assertCurrent()
  const { data: found } = await lookupClient.get('users/me', { params: { ms_graph_id: identity.microsoftId } })
  assertCurrent()
  if (!found || !Number.isInteger(found.id) || found.id <= 0 ||
      typeof found.api_key !== 'string' || !found.api_key ||
      typeof found.ms_graph_id !== 'string' || found.ms_graph_id.toLowerCase() !== identity.microsoftId.toLowerCase()) {
    throw new AuthMigrationError('ARC_ACCOUNT_NOT_FOUND', 'The existing ARC account could not be found by its Microsoft identifier. Contact the migration administrator.')
  }
  const baseline = { id: found.id, ms_graph_id: found.ms_graph_id, api_key: found.api_key }
  if (baselineState.account && Object.keys(baseline).some(field => baseline[field] !== baselineState.account[field])) {
    throw new AuthMigrationError('ARC_ACCOUNT_CHANGED', 'ARC’s account identifiers or credentials changed between attempts. Contact the migration administrator.')
  }
  baselineState.account = baseline
  const client = createUserClient(baseline.api_key)
  const readCurrent = async () => {
    const { data } = await client.get('users/me')
    assertCurrent()
    return assertAccount(data, baseline)
  }
  const current = await readCurrent()
  if (!needsUpdate(current, identity.googleId)) return current
  assertFresh()
  assertCurrent()

  let patchFailure
  try {
    const { data } = await client.patch('users/me', { google_id: identity.googleId })
    assertCurrent()
    // Partial/lost responses are reconciled by GET. Explicitly changed fields block.
    if (data) {
      for (const field of ['id', 'ms_graph_id', 'api_key']) {
        if (Object.prototype.hasOwnProperty.call(data, field) && data[field] !== baseline[field]) {
          throw new AuthMigrationError('ARC_ACCOUNT_CHANGED', 'ARC returned changed account identifiers or credentials. Contact the migration administrator.')
        }
      }
      if (Object.prototype.hasOwnProperty.call(data, 'google_id')) needsUpdate(data, identity.googleId)
    }
  } catch (error) {
    if (error instanceof AuthMigrationError) throw error
    patchFailure = error
  }

  const confirmed = await readCurrent()
  if (!needsUpdate(confirmed, identity.googleId)) return confirmed
  if (patchFailure) throw requestError(patchFailure, 'ARC could not save your Google ID. Retry or contact the administrator if this continues.')
  throw new AuthMigrationError('GOOGLE_UPDATE_UNCONFIRMED', 'ARC has not confirmed the Google ID update. Please try again.', { retryable: true, action: 'retry' })
}
